'use client'

import Link from 'next/link'
import { useTranslations } from 'next-intl'
import React, { useState, useRef, useMemo, memo, useCallback } from 'react'

import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger
} from '@/_components/ui/accordion'
import { getIcon } from '@/_lib/iconsMap.const'
import { IPreventiveExamination, IAnnualProgram, IMedanteCreditProgram, IBenefit } from '@/_types'

import { BenefitBlocks } from './a-content-card-content-editorjs-renderer'

interface IProps {
	program: IPreventiveExamination | IAnnualProgram | IMedanteCreditProgram
}

const BenefitItem = memo(({ benefit }: { benefit: IBenefit }) => {
	const iconSrc = useMemo(() => getIcon(benefit.icon), [benefit.icon])

	return (
		<li className='item'>
			<h4>
				<strong>{benefit.name}</strong>
			</h4>
			<BenefitBlocks data={benefit.description} id={benefit.id} />
			{iconSrc && <img src={iconSrc as string} alt={benefit.icon} />}
		</li>
	)
})

BenefitItem.displayName = 'BenefitItem'

const AContentCardContent = memo(({ program }: IProps) => {
	const t = useTranslations('pages.preventiveExaminationsCategories.overview')
	const [showFullContent, setShowFullContent] = useState(false)
	const contentRef = useRef<HTMLUListElement>(null)

	const handleExpand = useCallback((e: React.MouseEvent, isFullContent: boolean) => {
		e.preventDefault()
		setShowFullContent(prev => !prev)

		if (isFullContent === false) {
			const closestParentChild = (e.target as HTMLElement).closest('.item_complex-health')

			if (closestParentChild) {
				closestParentChild.scrollIntoView({ behavior: 'instant' })
			}
		}
	}, [])

	const sortedBenefits = useMemo(() => {
		return program.benefits.sort((a, b) => a.sortOrder - b.sortOrder)
	}, [program.benefits])

	const contentStyle = useMemo(
		() => ({
			maxHeight: showFullContent ? contentRef.current?.scrollHeight + 'px' : '277px',
			maskImage: !showFullContent
				? 'linear-gradient(to bottom, white 50%, transparent 100%)'
				: 'none',
			WebkitMaskImage: !showFullContent
				? 'linear-gradient(to bottom, white 40%, transparent 100%)'
				: 'none'
		}),
		[showFullContent]
	)

	const benefitItems = useMemo(
		() =>
			sortedBenefits.map((benefit: IBenefit, index: number) => (
				<BenefitItem key={`${program.id}-benefit-${index}`} benefit={benefit} />
			)),
		[sortedBenefits, program.id]
	)

	return (
		<Accordion
			type='single'
			collapsible
			defaultValue={program.isExpanded ? 'item-1' : undefined}
			suppressHydrationWarning={true}
		>
			<AccordionItem value='item-1'>
				<AccordionTrigger className='px-6 text-base font-semibold border-b-2 border-b-primary rounded-b-none'>
					{t('contains')}
				</AccordionTrigger>
				<AccordionContent>
					<ul
						className={`item_complex-health_text full-contect transition-all duration-300  ${
							showFullContent ? 'expanded' : ''
						}`}
						ref={contentRef}
						style={contentStyle}
						suppressHydrationWarning
					>
						{benefitItems}
					</ul>
					<div className='item_complex-health_bottom text-medante-purple-dark'>
						<Link
							href='#'
							onClick={e => handleExpand(e, showFullContent !== true)}
							className={`${showFullContent ? 'reverse' : ''} mx-auto`}
							title={t('moreInfo')}
							prefetch={false}
						>
							<span>
								<svg className='sico'>
									<use href='/assets/layout/icons.svg#s_ar1_d' />
								</svg>
							</span>
							<span className={showFullContent ? 'hide' : 'show'}>
								{t('moreInfo')}
							</span>
							<span className={showFullContent ? 'show' : 'hide'}>
								{t('lessInfo')}
							</span>
						</Link>
					</div>
				</AccordionContent>
			</AccordionItem>
		</Accordion>
	)
})

AContentCardContent.displayName = 'AContentCardContent'

export default AContentCardContent
